import { intuitiveFromNow, ResponsiveDialog } from '@cibo/ui'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Can } from '../Can'
import {
  useAuth,
  useFirstVisit,
  USER_DATA_KEYS,
  useUpdateUserData,
  useAllUserData,
} from '../../hooks'
import { useEffect, useState } from 'react'
import { AuthUserPermission } from '../../types'
import { LoadingButton } from '@mui/lab'

export const DebugProfile = () => {
  const { userId } = useAuth()
  const firstVisit = useFirstVisit()
  const [open, setOpen] = useState(false)
  const updateUserData = useUpdateUserData()
  const allUserData = useAllUserData()
  const [date, setDate] = useState<Date | null>()

  useEffect(() => {
    if (firstVisit.data) {
      setDate(new Date(firstVisit.data))
    } else {
      setDate(null)
    }
  }, [firstVisit.data])

  const handleUpdateFirstVisit = () => {
    updateUserData.mutate(
      {
        key: USER_DATA_KEYS.FIRST_LOGIN,
        value: !!date ? date.toISOString() : null,
      },
      {
        onSuccess: () => {
          firstVisit.refetch()
          allUserData.refetch()
        },
      }
    )
  }

  return (
    <Can useAny={AuthUserPermission.DEBUG_USER}>
      <Button onClick={() => setOpen(true)} variant="contained">
        User Data
      </Button>
      <ResponsiveDialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>DEBUG {userId}</DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
              <Stack>
                <Typography variant="caption">First Login</Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateTimePicker
                      value={date}
                      onChange={setDate}
                      renderInput={params => <TextField {...params} />}
                    />
                  </LocalizationProvider>
                  <LoadingButton
                    size="medium"
                    variant="contained"
                    disabled={!!date ? isNaN(date.valueOf()) : false}
                    loading={updateUserData.isLoading}
                    onClick={handleUpdateFirstVisit}
                  >
                    Update
                  </LoadingButton>
                </Stack>
                <Typography variant="caption">
                  {firstVisit.data !== undefined ? intuitiveFromNow(firstVisit.data) : 'Never'}
                </Typography>
              </Stack>
            </Stack>
            <Stack>
              <Typography>All User Data</Typography>
              <Box width="100%" overflow="auto" maxHeight={200}>
                <pre>{JSON.stringify(allUserData.data, null, 2)}</pre>
              </Box>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </Can>
  )
}
